import React, { useEffect, useState, useRef } from "react";
import { useDispatch, useSelector } from "react-redux";

import _ from "lodash";
import cx from "classnames";
import { convertDatesToTimezone } from "services/report.service";
import { CSVLink } from "react-csv";
import dateformat from "dateformat";

import { Button } from "react-bootstrap";
import { TableFooterInfo, TableHeaderInfo } from "..";
import { EmptyMessage, LoadingMessage, VisitorProfileView } from "../../reports/components";

import { sessionAnalyticsHeader } from "constants/workspaceReportsData";
import { StoryboardDetails } from ".";
import TruncatedTextWithTooltip from "components/TruncatedTextWithTooltip";
import { fetchingWorkspaceSessionAnalyics, fetchSessionAnalyticsForDownload, invalidateSessionAnalyticsDownloadData } from "actions/workspaceReports";
import { formatDurationWithLabel } from "../../reports/reporthelpers";

import styles from '../workspaceReports.module.scss'

export default function WorkspaceSessionAnalytics({ date, contentType, modalView = false, sectionId = "" }) {
	const dispatch = useDispatch();

	const [currentChart, setCurrentChart] = useState("table_view");
	const loading = useSelector(state => _.get(state, `workspaces.reports.sessions.loading`, false));
	const data = useSelector(state => _.get(state, `workspaces.reports.sessions.items`, []));
	const pagination = useSelector(state => _.get(state, 'workspaces.reports.sessions.pagination', {}));
	const totalCount = pagination.totalRecordsCount || 0;

	useEffect(() => {
		if (date || (contentType && contentType.length > 0)) {
			handlePageChange(0);
		}
	}, [date, contentType]);

	const handlePageChange = (page) => {
		handleSessionAnalytics(page);
	}

	const handleSessionAnalytics = (page) => {
		if (pagination.current != -1) {
			const request = {
				types: contentType,
				...convertDatesToTimezone(date),
			}

			dispatch(fetchingWorkspaceSessionAnalyics(request, page, 20));
		}
	}

	useEffect(() => {
		if (pagination.current == -1) {
			const request = {
				types: contentType,
				...convertDatesToTimezone(date),
			}
			dispatch(fetchingWorkspaceSessionAnalyics(request, 0, 20));
		}
	}, [pagination]);

	return (
		<div className={cx(styles.storyboardReportsWrapper, { [styles.modalWrapper]: modalView })} id={sectionId}>
			<div className={styles.storyboardReportsContainer}>
				<TableHeaderInfo title="Session Analytics" count={totalCount || 0} modalView={modalView} />
				<SessionAnalyticsReports
					data={data}
					modalView={modalView}
					date={date}
					count={totalCount}
					type={contentType}
					loading={loading}
				/>

				{/* {currentChart == "graph_view" &&
                    <div className={styles.graphView}>Graph view</div>
                } */}
			</div>
			<TableFooterInfo
				data={data}
				currentChart={currentChart}
				disableView={['pie_chart_view']}
				modalView={modalView}
				onChange={handlePageChange}
				type='sessionAnalytics'
				pagination={pagination}
				paginationName={"Sessions"}
				date={date}
				contentType={contentType}
			/>
		</div>
	)
}

export const SessionAnalyticsReports = ({ data, modalView, date, type, loading }) => {

	const sessionData = useSelector(state => {
		const records = Array.isArray(data) ? data : [];
		return modalView ? records : records.slice(0, 5);
	});

	return (
		<>
			<div className={cx(styles.storyboardReportsView, { [styles.tableDataOverflow]: modalView })}>
				<div className={cx(styles.tableHeaderWrapper, { [styles.sticky]: modalView })}>
					<div className={styles.downloadOption}>
						<ExportCSV date={date} type={type} />
					</div>
				</div>
				<table className={cx(styles.sessionAnalyticsTable)}>
					<thead className={cx(styles.tableHeader, { [styles.stickyHeader]: modalView })}>
						{sessionAnalyticsHeader.map((header) => {
							return header
						})}
					</thead>
					{loading &&
						<div className={cx(styles.loader, { [styles.modalLoader]: modalView })}>
							<LoadingMessage className={styles.loadingMessage} />
						</div>
					}
					{!loading && data.length == 0 &&
						<div className={cx(styles.loader, { [styles.modalLoader]: modalView })}>
							<EmptyMessage />
						</div>
					}
					{!loading && data && data.length > 0 &&
						<tbody className={styles.tableBody}>
							{sessionData.map((session) => (
								<>
									<tr className={styles.tableBodyDataRow}>
										<td className={styles.tableBodyData}>
											<VisitorProfileView
												data={session}
												type="sessions"
											/>
										</td>
										<td className={styles.tableBodyData}>
											<StoryboardDetails data={session} />
										</td>
										<td className={styles.tableBodyData}>
											<div className={styles.source}>
												<TruncatedTextWithTooltip className={styles.url} content={_.get(session, 'campaign.source') || '-'} maxWidth={250} placement='top' />
												<TruncatedTextWithTooltip className={styles.url} content={_.get(session, 'campaign.medium' || '')} maxWidth={250} placement='top' />
											</div>
											<TruncatedTextWithTooltip className={styles.url} content={session.url} maxWidth={250} placement='top' />
										</td>
										<td className={styles.tableBodyData}>
											<div className={styles.geography}>
												{session.country?.city},
												{session.country?.region_name},
												{session.country?.country_code}
											</div>
											<div className={styles.devices}>
												{session.browser} on {session.device}
											</div>
										</td>
										<td className={styles.tableBodyData}>
											<div className={styles.timeSpent}>{formatDurationWithLabel(session.timeSpent).value}</div>
											<div className={styles.completion}>{session.completion}%</div>
										</td>
									</tr>
								</>
							))}
						</tbody>
					}
				</table>
			</div>
		</>
	)
}

function ExportCSV({ date, type }) {
	const dispatch = useDispatch();

	const downloads = useSelector(state => _.get(state, 'workspaces.download.sessions.items', []));
	const completed = useSelector(state => _.get(state, 'workspaces.download.sessions.completed'));
	const progress = useSelector(state => _.get(state, 'workspaces.download.sessions.progress'));
	const pagination = useSelector(state => _.get(state, 'workspaces.download.sessions.pagination', {}));
	const [csvData, setCsvData] = useState([]);
	const csvLink = useRef();

	const headers = [
		{ label: "Visitor Name", key: "visitorName" },
		{ label: "Email", key: "email" },
		{ label: "Visitor Last Visited", key: "lastVisited" },
		{ label: "Storyboard Name", key: "storyboardName" },
		{ label: "Storyboard Type", key: "contentType" },
		{ label: "Storyboard Last Published", key: "lastPublished" },
		{ label: "PageUrl", key: "pageUrl" },
		{ label: "City", key: "city" },
		{ label: "State", key: "state" },
		{ label: "Country", key: "country" },
		{ label: "Device", key: "device" },
		{ label: "TimeSpent", key: "timeSpent" },
		{ label: "Completion", key: "completion" },
	];

	useEffect(() => {
		if (completed) {
			var columns = [];
			downloads && downloads.map((data, index) => {
				columns[index] = {
					visitorName: _.get(data, 'visitor.firstName', "-"),
					email: _.get(data, 'visitor.email', "-"),
					lastVisited: data.lastVisited == undefined ? "" : dateformat(data.lastVisited, 'dd mmm yyyy'),
					storyboardName: _.get(data, 'storyboardResponse.name', "-"),
					contentType: _.get(data, 'storyboardResponse.type', "-"),
					lastPublished: data.lastPublished == undefined ? "" : dateformat(data.lastPublished, 'dd mmm yyyy'),
					pageUrl: _.get(data, 'url', "-"),
					city: _.get(data, 'country.city', '') || '',
					state: _.get(data, 'country.region_name', '') || '',
					country: _.get(data, 'country.country_name', '') || '',
					device: _.get(data, 'device', "-"),
					timeSpent: formatDurationWithLabel(data.timeSpent).value || 0,
					completion: data.completion + "%" || 0,
				}
			});

			setCsvData(columns);
		}
	}, [completed]);

	useEffect(() => {
		if (!completed && csvData && csvData.length == 0) {
			return;
		}

		csvLink.current.link.click();
		dispatch(invalidateSessionAnalyticsDownloadData());
	}, [csvData]);

	const fetchSessionAnalyticstData = () => {
		dispatch(invalidateSessionAnalyticsDownloadData());

		const request = {
			types: type,
			...convertDatesToTimezone(date),
		}
		dispatch(fetchSessionAnalyticsForDownload(request, 0, 200))
	}

	return (
		<div>
			<Button variant="transparent" disabled={progress} onClick={() => fetchSessionAnalyticstData()} className={styles.download}>
				<i className={cx("cleverstory-download", styles.downloadIcon)} />
				<span className={styles.progress}>{progress ? "Downloading..." : "Download "}</span>
			</Button>
			<CSVLink
				ref={csvLink}
				headers={headers}
				data={csvData}
				filename='sessionAnalyticsReports.csv'
				className='hidden'
			/>
		</div>
	)
}